import { StatusName } from "../../../../factories/statuses/enums/status.enum";
import { UpdateWorkTypeStatusModel } from "../../../../slices/WorkTypeSlice";
import { BillableDefaultChoice } from "./workTypeEditor.types";

export const HIDDEN_WORK_TYPE_STATUS_NAMES: readonly StatusName[] = [
  StatusName.trial,
  StatusName.Scheduled,
] as const;

export const getWorkTypeStatusesSignature = (
  statuses: UpdateWorkTypeStatusModel[],
) =>
  JSON.stringify(
    statuses
      .map((status) => ({
        workTypeStatusId: status.workTypeStatusId,
        workTypeSubStatusIds: [...status.workTypeSubStatusIds].sort((a, b) =>
          String(a).localeCompare(String(b)),
        ),
      }))
      .sort((a, b) =>
        String(a.workTypeStatusId).localeCompare(String(b.workTypeStatusId)),
      ),
  );

export const toBillableChoice = (
  value: boolean | null | undefined,
): BillableDefaultChoice =>
  value === true ? "billable" : value === false ? "nonBillable" : "none";

export const fromBillableChoice = (choice: BillableDefaultChoice) =>
  choice === "billable" ? true : choice === "nonBillable" ? false : null;

// Same limits as the database / server (Service.Name 20, Description 50).
export const WORK_TYPE_NAME_MAX = 20;
export const WORK_TYPE_DESCRIPTION_MAX = 50;

/**
 * The server's own sentence for a refused request (400: e.g. "Work type name
 * should not be longer than 20 characters.", or "… is used by work items …"
 * when deleting), otherwise the fallback. 5xx answers always use the fallback.
 */
export const serverErrorMessage = (error: unknown, fallback: string) => {
  const err = error as
    | { status?: unknown; originalStatus?: unknown; data?: unknown }
    | null
    | undefined;
  // The server answers most 400s as plain text (`BadRequest("...")`), which
  // RTK Query reports as status "PARSING_ERROR" with the real status in
  // `originalStatus` and the text in `data` (QA 2026-10-21: duplicate work
  // type names showed "Something went wrong").
  const status =
    typeof err?.status === "number"
      ? err.status
      : err?.status === "PARSING_ERROR" &&
        typeof err?.originalStatus === "number"
      ? err.originalStatus
      : 0;
  if (status < 400 || status >= 500) return fallback;
  const data = err?.data;
  const text =
    typeof data === "string"
      ? data
      : typeof (data as { message?: unknown })?.message === "string"
      ? (data as { message: string }).message
      : "";
  const clean = text.trim();
  // A real sentence only (not an HTML page or a JSON dump).
  if (!clean || clean.length > 300 || /^[<{[]/.test(clean)) return fallback;
  return clean;
};
